<div class="project-reports container-fluid mb-3">
  @if (loading) {
    <div class="row">
      <mat-progress-bar mode="indeterminate" class="custom-loader"></mat-progress-bar>
    </div>
  }

  <div class="dashboard-card shadow-sm my-3">
    <div class="card-header bg-white border-bottom p-4">
      <h4 class="dashboard-card-title text-primary-purple mb-1 d-flex align-items-center">
        <mat-icon class="dashboard-title-icon">folder_open</mat-icon>
        {{ 'components.project-reports.title' | translate }}
      </h4>
      <p class="text-muted mb-3">{{ 'components.project-reports.description' | translate }}</p>
      <div class="d-flex stats-row">
        <div class="stat-item mr-5">
          <h2 class="mb-0 font-weight-bold text-primary-purple">{{ dataSource.data.length }}</h2>
          <small class="stat-label">{{ 'components.project-reports.total-documents' | translate }}</small>
        </div>
      </div>
    </div>

    <div class="card-body p-0">
      <div class="row justify-content-center card-details-buttons">
        <div class="col-12 d-flex justify-content-end align-items-center flex-wrap gap-2">
          @if (canManage) {
            <button mat-stroked-button class="text-dark-purple border-purple" (click)="openAdd()">
              <mat-icon class="mr-1">upload_file</mat-icon>
              {{ 'components.project-reports.add-document' | translate }}
            </button>
            <div class="vertical-divider mx-2"></div>
          }
          <button mat-stroked-button class="text-dark-purple border-purple" [matMenuTriggerFor]="columnMenu">
            <mat-icon class="mr-1">view_column</mat-icon>
            {{ 'generic.columns' | translate }}
          </button>
          <mat-menu #columnMenu="matMenu" class="column-picker-menu">
            <div class="column-picker-header p-3 border-bottom">
              <h6 class="mb-0 font-weight-bold">{{ 'components.surveys.survey-team.survey-team.customize-columns' | translate }}</h6>
            </div>
            <div class="column-picker-body py-2">
              @for (setting of columnSettings; track setting.key) {
                <div class="px-3 py-1 dropdown-item-container" (click)="$event.stopPropagation()">
                  <mat-checkbox color="primary" [checked]="setting.visible" (change)="toggleColumn(setting)">
                    <span class="small">{{ setting.label | translate }}</span>
                  </mat-checkbox>
                </div>
              }
            </div>
          </mat-menu>
        </div>
      </div>

      <div class="px-4 py-3 border-top bg-white">
        <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <h6 class="text-primary-purple font-weight-bold mb-0 d-flex align-items-center">
            <mat-icon class="mr-2">filter_list</mat-icon>
            {{ 'generic.filters' | translate }}
          </h6>
          <div class="d-flex align-items-center">
            <small class="text-muted mr-3">{{ 'components.project-reports.filters-results' | translate: { shown: dataSource.filteredData.length, total: dataSource.data.length } }}</small>
            @if (hasActiveFilters) {
              <button mat-button color="primary" type="button" (click)="resetFilters()">
                <mat-icon class="mr-1">filter_alt_off</mat-icon>
                {{ 'generic.reset-filters' | translate }}
              </button>
            }
          </div>
        </div>

        <div class="row g-3">
          <div class="col-md-6 col-lg-4">
            <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
              <mat-label>{{ 'components.project-reports.search' | translate }}</mat-label>
              <mat-icon matPrefix class="mr-2 text-muted search-prefix">search</mat-icon>
              <input matInput type="text" name="reports_search" [ngModel]="filters.search" (ngModelChange)="filters.search = $event; applyFilters()">
              @if (filters.search) {
                <button mat-icon-button matSuffix type="button" (click)="filters.search = ''; applyFilters()">
                  <mat-icon>close</mat-icon>
                </button>
              }
            </mat-form-field>
          </div>

          <div class="col-md-6 col-lg-3">
            <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
              <mat-label>{{ 'components.project-reports.filter-type' | translate }}</mat-label>
              <mat-select name="reports_filter_type" [(ngModel)]="filters.type" (selectionChange)="applyFilters()">
                <mat-option value="ALL">{{ 'generic.all' | translate }}</mat-option>
                @for (type of availableTypes; track type) {
                  <mat-option [value]="type">{{ 'components.project-reports.type-' + type | translate }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div class="col-md-6 col-lg-3">
            <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
              <mat-label>{{ 'components.project-reports.filter-uploader' | translate }}</mat-label>
              <mat-select name="reports_filter_uploader" [(ngModel)]="filters.uploader" (selectionChange)="applyFilters()">
                <mat-option value="ALL">{{ 'generic.all' | translate }}</mat-option>
                @for (uploader of uploaders; track uploader.id) {
                  <mat-option [value]="uploader.id">{{ uploader.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div class="col-md-6 col-lg-2">
            <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
              <mat-label>{{ 'components.project-reports.filter-period' | translate }}</mat-label>
              <mat-select name="reports_filter_period" [(ngModel)]="filters.period" (selectionChange)="applyFilters()">
                <mat-option value="ALL">{{ 'generic.all' | translate }}</mat-option>
                <mat-option value="7">{{ 'components.project-reports.period-7' | translate }}</mat-option>
                <mat-option value="30">{{ 'components.project-reports.period-30' | translate }}</mat-option>
                <mat-option value="90">{{ 'components.project-reports.period-90' | translate }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="dashboard-card reports-table-card">
    <mat-table [dataSource]="dataSource" matSort matSortActive="created_at" matSortDirection="desc" class="w-100 table-primary">
      <ng-container matColumnDef="title">
        <mat-header-cell *matHeaderCellDef mat-sort-header class="left col-title">{{ 'components.project-reports.column-title' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="left col-title">
          <mat-icon class="file-type-icon mr-2">{{ projectReportFileIcon(row.file_name) }}</mat-icon>
          <div class="report-info">
            <span class="report-title" [matTooltip]="row.title">{{ row.title }}</span>
            <span class="report-file text-muted small" [matTooltip]="row.file_name">{{ row.file_name }}</span>
          </div>
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="notes">
        <mat-header-cell *matHeaderCellDef class="left col-notes">{{ 'components.project-reports.column-notes' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="left col-notes">
          @if (row.notes) {
            <span class="report-notes" [matTooltip]="row.notes">{{ row.notes }}</span>
          } @else {
            <span class="text-muted small">-</span>
          }
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="type">
        <mat-header-cell *matHeaderCellDef mat-sort-header class="col-centered">{{ 'components.project-reports.column-type' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="col-centered">
          <span class="type-chip">{{ typeLabelKey(row.file_name) | translate }}</span>
        </mat-cell>
      </ng-container>

      <ng-container matColumnDef="file_size">
        <mat-header-cell *matHeaderCellDef mat-sort-header class="col-centered">{{ 'components.project-reports.column-size' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="col-centered">{{ formatSize(row.file_size) }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="uploaded_by_name">
        <mat-header-cell *matHeaderCellDef mat-sort-header class="col-centered">{{ 'components.project-reports.column-uploaded-by' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="col-centered">{{ row.uploaded_by_name || '-' }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="created_at">
        <mat-header-cell *matHeaderCellDef mat-sort-header class="col-centered">{{ 'components.project-reports.column-date' | translate }}</mat-header-cell>
        <mat-cell *matCellDef="let row" class="col-centered">{{ row.created_at | date: 'dd/MM/yyyy HH:mm' }}</mat-cell>
      </ng-container>

      <ng-container matColumnDef="actions">
        <mat-header-cell *matHeaderCellDef class="col-centered col-actions"></mat-header-cell>
        <mat-cell *matCellDef="let row" class="col-centered col-actions">
          <button mat-icon-button color="primary" [matTooltip]="'components.project-reports.action-view' | translate" (click)="view(row)">
            <mat-icon>visibility</mat-icon>
          </button>
          @if (row.can_edit) {
            <button mat-icon-button color="primary" [matTooltip]="'components.project-reports.action-edit' | translate" (click)="openEdit(row)">
              <mat-icon>edit</mat-icon>
            </button>
          }
          @if (row.can_delete) {
            <button mat-icon-button color="warn" [matTooltip]="'components.project-reports.action-delete' | translate" (click)="remove(row)">
              <mat-icon>delete</mat-icon>
            </button>
          }
        </mat-cell>
      </ng-container>

      <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
      <mat-row *matRowDef="let row; columns: displayedColumns;"></mat-row>
    </mat-table>

    @if (!loading && dataSource.data.length === 0) {
      <div class="empty-state">
        <mat-icon class="empty-icon">folder_off</mat-icon>
        <span class="text-muted d-block">{{ 'components.project-reports.empty' | translate }}</span>
      </div>
    } @else if (!loading && dataSource.filteredData.length === 0) {
      <div class="empty-state">
        <span class="text-muted">{{ 'components.project-reports.no-matches' | translate }}</span>
      </div>
    }

    <mat-paginator [pageSizeOptions]="[5, 10, 25, 50]" [pageSize]="10" showFirstLastButtons></mat-paginator>
  </div>
</div>
